import {
  type ColumnsType,
  CountAmountCell,
  Divider,
  Empty,
  Flex,
  Loading,
  Table,
  Text,
  WarningIcon,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { usePriorIdFromLineage } from '@/features/project/_hooks/project';
import { useProject } from '@/features/project/_hooks/project';
import { isBefore2025 } from '@/features/project/_utils/project';
import {
  BusinessFormLabelModifier,
  type ProjectModel,
  businessForm,
} from '@/features/project/projects/_models/project';
import { useFundBusinesses } from '@/features/project/projects/detail/fund-bussiness/_hooks/fund-business';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { TABLE_BUTTON_WIDTH, formatValueByUnit, isPS } from '@/utils';
import { groupBy } from 'lodash-es';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type {
  FundBusinessModel,
  FundBusinessesModel,
} from '../_model/fund-business';
import { FundBusinessForInvestmentFormDrawer } from './FundBusinessForInvestmentFormDrawer';
import { FundBusinessForInvestmentTableActions } from './FundBusinessForInvestmentTableActions';

type Project = z.infer<typeof ProjectModel>;

type Props = Pick<Project, 'fiscalYear'> & {
  editable: Project['isLocked'];
};

export const FundBusinessForInvestmentInformation: FC<Props> = ({
  fiscalYear,
  editable,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const fundBusinesses = useFundBusinesses({ projectId });
  const priorProjectId =
    usePriorIdFromLineage({
      params: {
        fiscalYear: project.data?.fiscalYear,
        lineageId: project.data?.lineageId ?? '',
      },
      enabled: !isPS(),
    }).data?.priorProjectId ?? '';
  const priorFundBusinesses = useFundBusinesses({ projectId: priorProjectId });
  const editPermission = useProjectPermission(true);

  const orderedFundBusinesses = useMemo(() => {
    if (!fundBusinesses.data) {
      return null;
    }

    if (isBefore2025(project.data)) {
      return fundBusinesses.data;
    }

    const businessFormToFundBusinessesMap: {
      [k in z.infer<typeof businessForm>]?: z.infer<typeof FundBusinessesModel>;
    } = {};

    fundBusinesses.data.map((fundBusiness) => {
      businessFormToFundBusinessesMap[fundBusiness.businessForm] = [
        ...(businessFormToFundBusinessesMap[fundBusiness.businessForm] ?? []),
        fundBusiness,
      ];
    });

    return businessForm.options.flatMap(
      (option) => businessFormToFundBusinessesMap[option] ?? [],
    );
  }, [fundBusinesses.data, project.data]);

  if (fundBusinesses.isPending) {
    return <Loading />;
  }

  const isEmpty = !orderedFundBusinesses || orderedFundBusinesses.length === 0;

  const getYearColumns = () => {
    const indexedPerformances = fundBusinesses.data?.[0]?.performances.map(
      (performance, index) => ({
        ...performance,
        year: fiscalYear - Math.floor(index / 2),
        index,
      }),
    );
    const performancesByYear = groupBy(indexedPerformances, 'year');
    const columns: ColumnsType<z.infer<typeof FundBusinessModel>> =
      Object.entries(performancesByYear).map(([year, performances]) => {
        const isEstimatedYear = Number(year) >= fiscalYear;

        return {
          title: isEstimatedYear ? t('estimate-with-year', { year }) : year,
          children: performances.map((performance) => ({
            title: performance.title,
            render: (_, record) => {
              const currentPerformance = record.performances.at(
                performance.index,
              );
              const yearAmountInfo = currentPerformance?.yearAmountInfos[0];
              const amountInfo = yearAmountInfo?.amountInfo;

              if (!amountInfo) {
                return null;
              }
              const priorFundBusiness = priorFundBusinesses.data?.find(
                (priorFundBusiness) => priorFundBusiness.id === record.priorId,
              );
              const priorAmountInfo = (() => {
                if (
                  !priorFundBusiness ||
                  isBefore2025(project.data) ||
                  isPS()
                ) {
                  return;
                }
                if (performance.index === 4) {
                  return priorFundBusiness?.performances[2]?.yearAmountInfos[0]
                    ?.amountInfo;
                }
                if (performance.index === 5) {
                  return priorFundBusiness?.performances[3]?.yearAmountInfos[0]
                    ?.amountInfo;
                }
              })();
              if (
                priorAmountInfo &&
                ((priorAmountInfo.count ?? 0) !== (amountInfo.count ?? 0) ||
                  (priorAmountInfo.amount ?? 0) !== (amountInfo.amount ?? 0))
              ) {
                return (
                  <Flex justify="space-between">
                    <Text text={`(${formatValueByUnit(amountInfo.count)})`} />
                    <Flex gap={4}>
                      <WarningIcon
                        hint={t(
                          'warning-message-different-from-reference-value',
                        )}
                      />
                      <Text
                        text={formatValueByUnit(amountInfo.amount, {
                          operation: 'round',
                          unit,
                        })}
                      />
                    </Flex>
                  </Flex>
                );
              }

              return (
                <CountAmountCell
                  count={amountInfo.count}
                  amount={amountInfo.amount}
                  unit={unit}
                />
              );
            },
          })),
        };
      });

    return columns;
  };

  const yearColumns = getYearColumns();

  return (
    <Flex vertical gap={8}>
      <Flex align="center">
        <Text variant="h5" text={t('fund-business-regarding-support-title')} />
        <Divider type="vertical" />
        <Text
          variant="h5"
          color="secondary"
          text={`（${t('count')}）${t('amount')}`}
        />
      </Flex>
      <PinpointMarker
        pinpoint={{
          path: { project: projectId },
          field: 'ks.investment.fund-business-regarding',
        }}
      >
        {isEmpty ? (
          <Empty
            description={t('description-fund-business-investment-not-found')}
          />
        ) : (
          <Table
            bordered
            pagination={false}
            rowKey="id"
            tableLayout="fixed"
            dataSource={orderedFundBusinesses}
            columns={[
              {
                title: t('fund-business-investment-type'),
                key: 'businessForm',
                dataIndex: 'businessForm',
                render: (businessForm) =>
                  BusinessFormLabelModifier[businessForm],
              },
              {
                title: t('fund-business-title'),
                key: 'title',
                dataIndex: 'title',
              },
              ...yearColumns,
              ...(editPermission.data && editable
                ? ([
                    {
                      title: '',
                      key: 'actions',
                      align: 'center',
                      width: TABLE_BUTTON_WIDTH,
                      fixed: 'right',
                      render: (_, record) => (
                        <PublishedProjectMutableGuard>
                          <FundBusinessForInvestmentTableActions
                            fundBusinessId={record.id}
                          />
                        </PublishedProjectMutableGuard>
                      ),
                    },
                  ] satisfies ColumnsType<z.infer<typeof FundBusinessModel>>)
                : []),
            ]}
          />
        )}
      </PinpointMarker>
      <FundBusinessForInvestmentFormDrawer />
    </Flex>
  );
};
